<template>
  <ReviewItemDetailsLayout
    :review-item="article"
    :expansion-panel="expansionPanel"
    :tooltip="studyIssues"
    :item-url="article.metadata.url"
  >
    <template #subtitle>
      <p
        class="text-sm text-neutral-text-subtitle"
        :class="{
          'line-clamp-1': !expansionPanel.isShown.value,
        }"
        v-html="formattedAuthors"
      />
    </template>

    <template #menu-actions>
      <EditArticle
        :id="article.id"
        :meta-data="{
          abstract: article.metadata.abstract ?? '',
          authors: [...article.metadata.authors],
          doi: article.metadata.doi ?? '',
          title: article.metadata.title ?? '',
          issueNumber: article.metadata.issueNumber ?? '',
          journal: article.metadata.journal ?? '',
          pagesNumber: article.metadata.pagesNumber ?? '',
          publishYear: article.metadata.publishYear ?? '',
          volumeNumber: article.metadata.volumeNumber ?? '',
        }"
      />
    </template>

    <template #expanded-header>
      <div class="flex gap-2">
        <a
          class="underline flex items-center"
          :href="article.metadata.url"
          target="_blank"
          @click.stop
        >
          <SourceIcon :review-item="article" />
        </a>

        <div v-if="!!article.metadata.doi" class="flex items-center hover:brightness-125">
          <a
            class="flex items-center underline text-primary-text-default"
            :href="article.DoiLink"
            target="_blank"
            @click.stop
          >
            <ExternalLinkIcon class="w-5 h-5" /> DOI
          </a>
        </div>
      </div>
    </template>

    <template #expanded-content>
      <div class="flex justify-between gap-2">
        <div
          class="relative w-full bottom-0 right-0 rounded-lg bg-neutral-background-default transition-colors duration-1000 ease-in-out group-hover:bg-neutral-background-secondary"
        >
          <svg class="absolute inset-0 w-full h-full pointer-events-none" fill="none">
            <rect
              x="0.5"
              y="0.5"
              width="calc(100% - 1px)"
              height="calc(100% - 1px)"
              rx="8"
              ry="8"
              stroke="currentColor"
              stroke-width="1"
              stroke-dasharray="4 4"
              class="text-neutral-border-default transition-colors duration-1000 group-hover:text-primary-text-default"
            />
          </svg>

          <div class="p-3.5 text-sm text-neutral-text-body relative z-10">
            <p v-if="article.metadata.abstract" v-html="formattedAbstract"></p>
            <p v-else class="text-gray-400">Abstract not available</p>
          </div>
        </div>

        <div
          v-if="!article.metadata.abstract || !isAbstractValid"
          class="flex justify-between"
          @click.stop="markStudyAbstractAsNotAvailable(article.id, 'Abstract not available')"
        >
          <FloatingMenu trigger="hover" :offset="[0, 10]" placement="right">
            <template #activator>
              <div class="flex w-full px-2 py-1 sticky bottom-0 right-0 hover:brightness-110">
                <ExclamationIcon class="fill-yellow-300 text-yellow-700 w-5 h-5" />
              </div>
            </template>
            <template #content>
              <div class="px-2 py-1">
                Click here to confirm that study abstract is not available
              </div>
            </template>
          </FloatingMenu>
        </div>
      </div>
    </template>
  </ReviewItemDetailsLayout>
</template>

<script setup lang="ts">
import { computed } from "vue";
import ReviewItemDetailsLayout from "./ReviewItemDetailsLayout.vue";
import EditArticle from "./components/EditArticle.vue";
import SourceIcon from "./components/SourceIcon.vue";
import ExternalLinkIcon from "@app/components/Icons/ExternalLinkIcon.vue";
import ExclamationIcon from "@app/components/Icons/ExclamationIcon.vue";
import FloatingMenu from "@app/components/Global/FloatingMenu.vue";
import { Article } from "@core/domain/models/article.model";
import { applyHighlights } from "@app/utils/words-highlighter";
import useExpansionPanel from "@app/composables/use-expansion-panel";
import { injectStrict } from "@app/utils/injectStrict";
import { ReviewKey } from "@app/views/Review/use-review";
import { SnackbarState } from "@app/types";
import { Id } from "@core/domain/types/id.type";
import { HttpException } from "@core/exceptions/http.exception";
import { errorMessage } from "@app/utils/error-message";
import useLoading from "@app/composables/use-loading";
import useSnackbar from "@app/composables/use-snackbar";

const props = defineProps<{
  article: Article;
  expansionPanel: ReturnType<typeof useExpansionPanel>;
}>();

const review = injectStrict(ReviewKey);
const loading = useLoading();
const snackbar = useSnackbar();

const isAbstractValid = computed(() => {
  return (
    (props.article.metadata.abstract?.[0] !== "." ||
      props.article.metadata.abstract?.[1] !== "." ||
      props.article.metadata.abstract?.[2] !== ".") &&
    (props.article.metadata.abstract?.[props.article.metadata.abstract.length - 1] !== "." ||
      props.article.metadata.abstract?.[props.article.metadata.abstract.length - 2] !== "." ||
      props.article.metadata.abstract?.[props.article.metadata.abstract.length - 3] !== ".") &&
    props.article.metadata.abstract !== "N/A"
  );
});

const formattedAbstract = computed(() => {
  let abstract = props.article.metadata.abstract;
  if (!abstract) return "";

  if (review.filtering.terms.value.length > 0) {
    abstract = applyHighlights(abstract, review.filtering.terms.value);
  }
  const abstractTitlesRegex = /\b([A-Z][A-Z\s]*:)/gim;
  abstract = abstract.replaceAll("\n", "<br>");
  abstract = abstract.replace(abstractTitlesRegex, "<strong>$1</strong> ");
  return abstract;
});

const formattedAuthors = computed(() => {
  const authors = props?.article?.metadata?.authors ?? [];
  if (!Array.isArray(authors) || authors.length === 0) return "";

  const highlighted =
    review.filtering.terms.value.length > 0
      ? authors.map((author) => applyHighlights(author, review.filtering.terms.value))
      : authors;

  return highlighted.join(", ");
});

const studyIssues = computed(() => {
  let content = "";
  if (props.article.isDuplicate) return "";

  if (props.article.maybeDuplicate) {
    content += "\u{2022} Confirm/reject the duplication.<br>";
  }
  if (
    props.article.search?.source.id !== "clinical-trials-gov" &&
    props.article.search?.source.id !== "maude" &&
    props.article.search?.source.id !== "ictrp" &&
    (!props.article.metadata.publishYear || props.article.metadata?.authors?.length === 0)
  ) {
    content += "\u{2022} Author is missing and Publish year is missing.<br>";
  }
  if (!props.article.metadata.title) {
    content += "\u{2022} Title is missing.<br>";
  }
  if (!props.article.metadata.abstract || !isAbstractValid.value) {
    content += "\u{2022} Abstact missing or incomplete. Confirm/modify abstract.<br>";
  }
  return content;
});

async function markStudyAbstractAsNotAvailable(studyId: Id, abstract: string) {
  try {
    loading.start();
    await review.editStudyAbstract(studyId, abstract);
    snackbar.success("Success");
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
    if (error.response.data.statusCode >= 500) {
      throw e;
    }
  } finally {
    loading.stop();
  }
}
</script>
